import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_CROP_SETTINGS,
  REEL_CROP_MODES,
  clampFocal,
  isWideSource,
  zoomedCropWidthFraction,
} from '../../utils/reelCrop';

function ModeButton({ active, onClick, disabled, title, description }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex-1 text-left p-4 rounded-xl border transition-all ${
        active
          ? 'border-brand-cyan bg-brand-cyan/10 ring-1 ring-brand-cyan/40'
          : 'border-[var(--border-subtle)] bg-[var(--bg-base)] hover:border-brand-cyan/50'
      } disabled:opacity-50`}
    >
      <span className="block text-[10px] font-black uppercase tracking-widest text-[var(--text-main)] mb-1">
        {title}
      </span>
      <span className="block text-xs text-[var(--text-secondary)] leading-relaxed">{description}</span>
    </button>
  );
}

function FocalPointPicker({ videoSrc, aspectRatio, focalX, focalY, onChange, disabled }) {
  const containerRef = useRef(null);
  const draggingRef = useRef(false);
  const [frameReady, setFrameReady] = useState(false);

  const cropWidthFrac = zoomedCropWidthFraction(aspectRatio);
  const fx = focalX ?? 0.5;
  const fy = focalY ?? 0.5;

  const halfCrop = cropWidthFrac / 2;
  const cropLeft = clampFocal(fx - halfCrop);
  const cropRight = clampFocal(fx + halfCrop);
  const actualCenterX = (cropLeft + cropRight) / 2;

  const updateFromClient = useCallback((clientX, clientY) => {
    const el = containerRef.current;
    if (!el || disabled) return;
    const rect = el.getBoundingClientRect();
    const x = clampFocal((clientX - rect.left) / rect.width);
    const y = clampFocal((clientY - rect.top) / rect.height);
    onChange({ focalX: x, focalY: y });
  }, [disabled, onChange]);

  const handlePointerDown = (e) => {
    if (disabled) return;
    draggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    updateFromClient(e.clientX, e.clientY);
  };

  const handlePointerMove = (e) => {
    if (!draggingRef.current || disabled) return;
    updateFromClient(e.clientX, e.clientY);
  };

  const handlePointerUp = (e) => {
    draggingRef.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  useEffect(() => {
    setFrameReady(false);
  }, [videoSrc]);

  return (
    <div className="space-y-2">
      <p className="text-xs text-[var(--text-secondary)]">
        Arrastra o toca el recuadro para elegir qué parte del video permanece centrada en el feed vertical.
      </p>
      <div
        ref={containerRef}
        className="relative w-full rounded-xl overflow-hidden border border-[var(--border-subtle)] bg-black select-none touch-none"
        style={{ aspectRatio: `${aspectRatio} / 1` }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        role="presentation"
      >
        <video
          src={videoSrc}
          muted
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          onLoadedData={() => setFrameReady(true)}
        />

        {!frameReady && (
          <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold uppercase tracking-widest text-white/50">
            Cargando vista previa…
          </div>
        )}

        {/* Dimmed regions outside crop window */}
        <div
          className="absolute top-0 bottom-0 left-0 bg-black/55 pointer-events-none"
          style={{ width: `${cropLeft * 100}%` }}
        />
        <div
          className="absolute top-0 bottom-0 right-0 bg-black/55 pointer-events-none"
          style={{ width: `${(1 - cropRight) * 100}%` }}
        />

        {/* Crop window */}
        <div
          className="absolute top-0 bottom-0 border-2 border-brand-cyan shadow-[0_0_0_1px_rgba(0,0,0,0.4)_inset] pointer-events-none"
          style={{
            left: `${cropLeft * 100}%`,
            width: `${(cropRight - cropLeft) * 100}%`,
          }}
        >
          <div className="absolute inset-0 border border-white/30 border-dashed" />
          <div
            className="absolute w-3 h-3 -translate-x-1/2 -translate-y-1/2 rounded-full gfi-gradient-bg border-2 border-white shadow-lg"
            style={{
              left: `${((actualCenterX - cropLeft) / (cropRight - cropLeft || 1)) * 100}%`,
              top: `${fy * 100}%`,
            }}
          />
        </div>
      </div>
      <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider font-bold">
        Punto focal: {Math.round(actualCenterX * 100)}% horizontal · {Math.round(fy * 100)}% vertical
      </p>
    </div>
  );
}

export default function ReelCropSettings({
  videoSrc,
  aspectRatio,
  value = DEFAULT_CROP_SETTINGS,
  onChange,
  disabled = false,
  compact = false,
}) {
  if (!isWideSource(aspectRatio)) return null;

  const cropMode = value.cropMode ?? REEL_CROP_MODES.ZOOMED;
  const isZoomed = cropMode === REEL_CROP_MODES.ZOOMED;

  const setMode = (mode) => {
    if (mode === REEL_CROP_MODES.LETTERBOXED) {
      onChange({ cropMode: mode, focalX: null, focalY: null });
    } else {
      onChange({
        cropMode: REEL_CROP_MODES.ZOOMED,
        focalX: value.focalX,
        focalY: value.focalY,
      });
    }
  };

  const setFocal = ({ focalX, focalY }) => {
    onChange({ cropMode: REEL_CROP_MODES.ZOOMED, focalX, focalY });
  };

  return (
    <div className={`space-y-4 ${compact ? '' : 'pt-2 border-t border-[var(--border-subtle)]'}`}>
      {!compact && (
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-1">
            Encuadre en el feed
          </p>
          <p className="text-xs text-[var(--text-secondary)]">
            Tu video es horizontal. Elige cómo se verá en el feed vertical de reels.
          </p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <ModeButton
          active={isZoomed}
          disabled={disabled}
          onClick={() => setMode(REEL_CROP_MODES.ZOOMED)}
          title="Zoom (recortado)"
          description="Llena la pantalla vertical recortando los lados. Es el comportamiento actual por defecto."
        />
        <ModeButton
          active={!isZoomed}
          disabled={disabled}
          onClick={() => setMode(REEL_CROP_MODES.LETTERBOXED)}
          title="Letterbox (completo)"
          description="Muestra el frame completo con bandas arriba y abajo, sin recortar."
        />
      </div>

      {isZoomed && videoSrc && aspectRatio && (
        <FocalPointPicker
          videoSrc={videoSrc}
          aspectRatio={aspectRatio}
          focalX={value.focalX}
          focalY={value.focalY}
          onChange={setFocal}
          disabled={disabled}
        />
      )}
    </div>
  );
}
